// @ts-strict-ignore

export enum PersonalRoutineType {
    PERSONALROUTINE_ATWORK = "At work",
    PERSONALROUTINE_ONBREAK = "On break",
    PERSONALROUTINE_OUTOFOFFICE = "Out of office",
    PERSONALROUTINE_DND = "Do not disturb",
    PERSONALROUTINE_CUSTOM = "Custom",
}

export class PersonalRoutine {

    /**
     * @public
     * @property {string} A user routine unique identifier.
     * @readonly
     */
    public id: string;

    /**
     * @public
     * @property {string} Type of routine. Could be: "At work", "Out of office", "On break", "Do not disturb", "Custom"
     * @readonly
     */
    public type: PersonalRoutineType;

    /**
     * @public
     * @property {string} id of icon associated with routine type.
     * @readonly
     */
    public iconId: string;

    /**
     * @public
     * @property {string} Name of the routine
     */
    private _name: string;
    public get name(): string {
        return this.type === PersonalRoutineType.PERSONALROUTINE_CUSTOM ? this._name : this._name.replace(/ /g, "_");
    }

    public set name(value: string) {
        this._name = value;
    }

    /**
     * @public
     * @property {boolean} Specify whether this routine is activated or not.
     */
    public active: boolean = false;

    /**
     * @public
     * @property { { manage: boolean, value: string } } Presence mode data
     *      manage: Is presence mode managed on routine activation. Default value: false;
     *      value: Presence mode value, same choice(s) as in Rainbow GUI. Default value: "online". Allowed values: "online", "away", "dnd", "invisible"
     */
    public presence: { manage: boolean; value: string; } = { manage: false, value: "online" };

    /**
     * @public
     * @property { { manage: boolean; policy: string; phoneNumberId?: string } } CLIOptions data
     *      manage: Is CLI managed on routine activation. Default value: false;
     *      policy: Cli options policy. Default value: "company_policy". Allowed values: "company_policy", "installation_number", "user_ddi_number", "other_ddi_number"
     *      phoneNumberId: Phone number id in "other_ddi_number" policy
     */
    public cliOptions: { manage: boolean; policy: string; phoneNumberId?: string; anonymous?: boolean; } = { manage: false, policy: "company_policy", phoneNumberId: "", anonymous: false };

    /**
     * @public
     * @property { { manage: boolean, mode: string } } Device mode data
     *      manage: Is device mode managed on routine activation. Default value: false;
     *      mode: Device mode value, same choice(s) as in Rainbow GUI. Default value: "computer". Allowed values: "computer", "office_phone"
     */
    public deviceMode: { manage: boolean; mode: string; } = { manage: false, mode: "computer" };

    /**
     * @public
     * @property {CallForwardData} Immediate call forward data
     */
    public immediateCallForward: CallForwardData = { manage: false, callForwardType: "immediate", activate: false, number: null, name: "", destinationType: "doNotForward" };

    /**
     * @public
     * @property {CallForwardData} Busy call forward data
     */
    public busyCallForward: CallForwardData = { manage: false, callForwardType: "busy", activate: false, number: null, name: "", destinationType: "doNotForward" };

    /**
     * @public
     * @property {CallForwardData} No reply call forward data
     */
    public noreplyCallForward: CallForwardData = { manage: false, callForwardType: "noreply", activate: false, number: null, name: "", destinationType: "doNotForward" };

    /**
     * @public
     * @property {{ manage: boolean, withdrawAll: boolean, huntingGroupsWithdraw: { rvcpGroupId: string, status: string }[] }} Hunting groups configuration
     *      manage: Is huntingGroups mode managed on routine activation. Default value: false;
     *      withdrawAll: Not for work routine. Default value: true
     *      huntingGroupsWithdraw: Array of objects on user status in each hunting groups, only for work routine
     *          rvcpGroupId: Hunting group unique identifier
     *          status: User's status in the hunting group. Allowed values: active, idle
     */
    public huntingGroups: { manage: boolean; withdrawAll?: boolean; huntingGroupsWithdraw?: { rvcpGroupId: string; status: string; }[]; } = { manage: false };

    /**
     * @public
     * @property {{ manage: boolean, active: boolean, message: string } }} Absence management
     *      manage: Is outOfOffice managed on routine activation. Default value: false;
     *      active: Is out of office active or not when managed. Default value: false;
     *      message: Out of Office message. Default value: "";
     */
    public outOfOffice: { manage: boolean; active: boolean; message: string; } = { manage: false, active: false, message: "" };

    /**
     * @public
     * @property {{ manage: boolean, active: boolean, message: string } }} Absence management
     *      manage: Is customStatus managed on routine activation. Default value: false;
     *      active: Is out of office active or not when managed. Default value: false;
     *      status: custom status message. Default value: "";
     *      emoji: emoji illusstrating the status. Default value: "💬";
     *      expirationDate: expiration date of custom status. For routines, it is ALWAYS null or undefined as it does not have expiration time
     */
    public customStatus: { manage: boolean; active: boolean; status: string; emoji: string; } = { manage: false, active: false, status: "", emoji: "💬" };

    private constructor(
        id: string,
        name: string,
        type: PersonalRoutineType,
        active: boolean,
        presence?: { manage: boolean; value: string; },
        deviceMode?: { manage: boolean; mode: string; },
        immediateCallForward?: CallForwardData,
        busyCallForward?: CallForwardData,
        noreplyCallForward?: CallForwardData,
        huntingGroups?: { manage: boolean; withdrawAll?: boolean; huntingGroupsWithdraw?: { rvcpGroupId: string; status: string; }[]; },
        cliOptions?: { manage: boolean; policy: string; phoneNumberId?: string; anonymous?: boolean; },
        customStatus?: { manage: boolean; active: boolean; status: string; emoji: string; },
        iconId?: string
    ) {
        this.id = id;
        this.name = name;
        this.type = type;
        this.active = active;
        this.presence = presence;
        this.deviceMode = deviceMode;
        this.immediateCallForward = immediateCallForward;
        if (immediateCallForward && immediateCallForward.destinationType === null) {
            this.immediateCallForward.destinationType = "doNotForward"; // Apparently, that is what activate = false is ...
        }
        this.busyCallForward = busyCallForward;
        if (busyCallForward && busyCallForward.destinationType === null) {
            this.busyCallForward.destinationType = "doNotForward"; // Apparently, that is what activate = false is ...
        }
        this.noreplyCallForward = noreplyCallForward;
        if (noreplyCallForward && noreplyCallForward.destinationType === null) {
            this.noreplyCallForward.destinationType = "doNotForward"; // Apparently, that is what activate = false is ...
        }
        this.huntingGroups = huntingGroups;
        this.cliOptions = cliOptions;
        this.customStatus = customStatus;
        this.iconId = iconId;
    }

    public static createRoutineFromData(data: any): PersonalRoutine {
        const personalRoutine = new PersonalRoutine(
            data.id,
            data.name,
            data.type,
            data.active,
            data.presence,
            data.deviceMode,
            data.immediateCallForward,
            data.busyCallForward,
            data.noreplyCallForward,
            data.huntingGroups,
            data.cliOptions,
            data.customStatus,
            this.getIconIdByType(data.type)
        );
        return personalRoutine;
    }

    public updateRoutineFromData(data: any): void {
        if (data.active) { this.active = data.active; }
        if (data.presence) { this.presence = data.presence; }
        if (data.deviceMode) { this.deviceMode = data.deviceMode; }
        if (data.huntingGroups) { this.huntingGroups = data.huntingGroups; }
        if (data.name) { this.name = data.name; }
        if (data.type) {
            this.type = data.type;
            this.iconId = PersonalRoutine.getIconIdByType(data.type);
        }
        if (data.immediateCallForward) {
            this.immediateCallForward = data.immediateCallForward;
            if (data.immediateCallForward.destinationType === null) {
                this.immediateCallForward.destinationType = "doNotForward"; // Apparently, that is what activate = false is ...
            }
        }
        if (data.noreplyCallForward) {
            this.noreplyCallForward = data.noreplyCallForward;
            if (data.noreplyCallForward.destinationType === null) {
                this.noreplyCallForward.destinationType = "doNotForward"; // Apparently, that is what activate = false is ...
            }
        }
        if (data.busyCallForward) {
            this.busyCallForward = data.busyCallForward;
            if (data.busyCallForward.destinationType === null) {
                this.busyCallForward.destinationType = "doNotForward"; // Apparently, that is what activate = false is ...
            }
        }
        if (data.cliOptions) {
            this.cliOptions = data.cliOptions;
        }

        if (data.customStatus) {
            this.customStatus = data.customStatus;
            delete this.customStatus['expirationDate'];
        }
    }

    public static getIconIdByType(type: PersonalRoutineType): string {
        let iconId = "";
        switch (type) {
            case PersonalRoutineType.PERSONALROUTINE_ATWORK:
                iconId = "company";
                break;
            case PersonalRoutineType.PERSONALROUTINE_ONBREAK:
                iconId = "free_breakfast";
                break;
            case PersonalRoutineType.PERSONALROUTINE_OUTOFOFFICE:
                iconId = "company_out";
                break;
            case PersonalRoutineType.PERSONALROUTINE_DND:
                iconId = "dnd";
                break;
            case PersonalRoutineType.PERSONALROUTINE_CUSTOM:
            default:
                iconId = "timelapse";
                break;
        }
        return iconId;
    }
}

export interface CallForwardData {
    /**
     * @public
     * @property {boolean} Is forward managed on routine activation
     */
    manage: boolean;

    /**
     * @public
     * @property {string} Type of forward
     */
    callForwardType: string;

    /**
     * @public
     * @property {boolean} Is call forward activated
     */
    activate: boolean;

    /**
     * @public
     * @property {string} Forward destination number
     */
    number: string;

    /**
     * @public
     * @property {string} Forward destination type. Allowed values: "voicemail", "internalnumber", "externalnumber"
     */
    destinationType: string;

    /**
     * @public
     * @property {number} delay in ms for no reply forward. Only applicable for callForwardType = "noreply"
     */
    noReplyDelay?: number;

    /**
     * @public
     * @property {string} name name of forward destination
     */
    name?: string;
}
